import { Button } from 'components/ui/button';
import { Skeleton } from 'components/ui/skeleton';
import { Loader2Icon } from 'components/icons';
import { useState } from 'react';
import { AuditTrailEntryDto } from '@common/audit-trail/auditTrailTypes';
import { t } from 'utils/getTranslations';
import { AuditTrailEntryCard } from './AuditTrailEntryCard';
import { bucketByDate } from '@common/audit-trail/auditTrailFormat';
import { auditTrailDayLabels } from './auditTrailLabels';
import { AuditTrailTransactionCard } from './AuditTrailTransactionCard';
import { ValuePreview, ValuePreviewDialog } from './ValuePreviewDialog';

export function AuditTrailFeed({
    entries,
    groupedByTransaction,
    isInitialLoading,
    isFetchingMore,
    hasMore,
    onLoadMore,
    onTransactionClick,
    onRowIdClick,
    onAuthorClick,
}: {
    entries: AuditTrailEntryDto[];
    groupedByTransaction: boolean;
    isInitialLoading: boolean;
    isFetchingMore: boolean;
    hasMore: boolean;
    onLoadMore: () => void;
    onTransactionClick: (transactionId: string) => void;
    onRowIdClick: (rowId: string) => void;
    onAuthorClick: (authorId: number) => void;
}) {
    const [preview, setPreview] = useState<ValuePreview | null>(null);

    if (isInitialLoading) {
        return (
            <div className="new:gap-(--card-gap) flex flex-col gap-3">
                <Skeleton className="h-30 rounded-xl" />
                <Skeleton className="h-30 rounded-xl" />
                <Skeleton className="h-30 rounded-xl" />
            </div>
        );
    }

    if (entries.length === 0) {
        return (
            <div className="bg-paper text-grey-400 shadow-card mt-3 rounded-xl px-5 py-14 text-center text-sm">
                {t('audit-trail.no-changes')}
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-3">
            {groupedByTransaction ? (
                <AuditTrailTransactionCard
                    entries={entries}
                    onRowIdClick={onRowIdClick}
                    onAuthorClick={onAuthorClick}
                    onPreview={setPreview}
                />
            ) : (
                bucketByDate(entries, auditTrailDayLabels()).map((bucket) => (
                    <div key={bucket.label} className="flex flex-col gap-3">
                        <div className="bg-background after:from-bg text-grey-500 new:text-label new:tracking-[0.06em] sticky top-(--audit-header-h) z-[5] -mx-2 px-3 pt-3 pb-1 text-[11.5px] font-semibold tracking-[0.05em] uppercase after:pointer-events-none after:absolute after:inset-x-0 after:top-full after:h-3 after:bg-linear-to-b after:to-transparent after:content-['']">
                            {bucket.label}
                        </div>
                        <div className="new:gap-(--card-gap) flex flex-col gap-3">
                            {bucket.entries.map((entry) => (
                                <AuditTrailEntryCard
                                    key={entry.id}
                                    entry={entry}
                                    onTransactionClick={onTransactionClick}
                                    onRowIdClick={onRowIdClick}
                                    onAuthorClick={onAuthorClick}
                                    onPreview={setPreview}
                                />
                            ))}
                        </div>
                    </div>
                ))
            )}
            {!groupedByTransaction && hasMore && (
                <div className="flex justify-center pt-2">
                    <Button variant="outline" disabled={isFetchingMore} onClick={onLoadMore}>
                        {isFetchingMore && <Loader2Icon className="animate-spin" />}
                        {t('audit-trail.load-more')}
                    </Button>
                </div>
            )}
            <ValuePreviewDialog preview={preview} onClose={() => setPreview(null)} />
        </div>
    );
}
